import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Input, Skeleton } from '@/ui';
import { FormItem, IFormInstance, PhoneInput, FileUpload } from '@/components';
import { FormColumn, StyledWrapper, StyledButton } from '../Styled';
import { CheckmarkIcon } from '@/icons';
import { StyledInfoBlockTitle, StyledForm, StyledTitle } from './CreatePartnerFormStyles';
import CompanyDataFields from '../CompanyDataFields/CompanyDataFields';
import { getPartnerFormInitialValues } from '../../helpers/companyData';
import CategoryManagerField from '../CategoryManagerField/CategoryManagerField';
import AccountantFields from '../AccountantFields/AccountantFields';
import BankAccountsFields from '../BankAccountsFields/BankAccountsFields';

// Models
import { IPartnerProfile } from '@/models/partners.model';

// Helpers
import { PartnerFormFieldsNames, TPartnerFormValues } from '../../helpers/types';
import { usePartnerFormRules } from '../../hooks';

interface ICreatePartnerForm {
  handleSubmit: (values: TPartnerFormValues) => void;
  isLoading: boolean;
  form: IFormInstance;
  initialValues?: IPartnerProfile | null;
}

const CreatePartnerForm = ({ handleSubmit, isLoading, initialValues, form }: ICreatePartnerForm) => {
  const { t } = useTranslation(['partners', 'common']);
  const rules = usePartnerFormRules();

  const placeholder = t('common_placeholder_input', { ns: 'common' });

  if (isLoading) return <Skeleton />;

  return (
    <StyledWrapper>
      <StyledTitle>{t('partners_create_partner')}</StyledTitle>

      <StyledForm onFinish={handleSubmit} initialValues={getPartnerFormInitialValues(initialValues)} form={form}>
        <FormColumn>
          <StyledInfoBlockTitle>{t('partners_company_data')}</StyledInfoBlockTitle>

          <CompanyDataFields isNew initialValues={initialValues} />

          <StyledInfoBlockTitle>{t('partners_bank_information')}</StyledInfoBlockTitle>

          <BankAccountsFields isNew />

          <StyledInfoBlockTitle>{t('partners_accountant_contacts')}</StyledInfoBlockTitle>

          <AccountantFields isNew />

          <StyledInfoBlockTitle>{t('partners_authorized_person_data')}</StyledInfoBlockTitle>

          <FormItem
            name={PartnerFormFieldsNames.FIRST_NAME}
            label={t('partners_contact_person_name')}
            rules={rules[PartnerFormFieldsNames.FIRST_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.LAST_NAME}
            label={t('partners_contact_person_last_name')}
            rules={rules[PartnerFormFieldsNames.LAST_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.MIDDLE_NAME}
            label={t('partners_contact_person_middle_name')}
            rules={rules[PartnerFormFieldsNames.MIDDLE_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.EMAIL}
            label={t('partners_email')}
            rules={rules[PartnerFormFieldsNames.EMAIL]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.PHONE}
            label={t('partners_phone_number')}
            rules={rules[PartnerFormFieldsNames.PHONE]}
          >
            <PhoneInput />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.SUBSCRIBER_FULL_NAME}
            label={t('partners_subscriber_full_name')}
            rules={rules[PartnerFormFieldsNames.SUBSCRIBER_FULL_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem name={PartnerFormFieldsNames.DOCUMENT} label={t('partners_document')}>
            <FileUpload />
          </FormItem>

          <StyledInfoBlockTitle>{t('partners_additional_info')}</StyledInfoBlockTitle>

          <CategoryManagerField label={t('partners_category_manager_optional')} />
        </FormColumn>

        <StyledButton htmlType="submit" loading={isLoading}>
          <CheckmarkIcon />
          {t('partners_create_partner')}
        </StyledButton>
      </StyledForm>
    </StyledWrapper>
  );
};

export default CreatePartnerForm;
